import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useResultsData } from "@/hooks/useResultsData";
import { getAmIOKScoreText } from "@/utils/resultsUtils";
import ResultsHeader from "@/components/results/ResultsHeader";
import ClinicalDisclaimerPanel from "@/components/ClinicalDisclaimerPanel";
import RiskScoreGauge from "@/components/RiskScoreGauge";
import { Database, Info, ChevronDown, ChevronUp } from "lucide-react";
import DetailButton from "@/components/results/DetailButton";
import SymptomsRecap from "@/components/results/SymptomsRecap";
import AmIOKScoringExplanation from "@/components/results/AmIOKScoringExplanation";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface AssessmentResult {
  urgency: "emergency" | "urgent" | "routine" | "self-care";
  category: string;
  recommendation: string;
  analysis: string;
  nextSteps: string[];
  professionalType?: string;
  amIOKScore: number;
  scientificReasoning: string;
  riskFactors: string[];
  protectiveFactors: string[];
  contacts?: Array<{
    name: string;
    number: string;
    description: string;
  }>;
  resources?: Array<{
    title: string;
    url: string;
  }>;
}
const ResultsPage1 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const answers = location.state?.answers || {};
  const result = location.state?.result as AssessmentResult;
  const {
    enhancedData,
    diagnosticAnalysis,
    isLoadingKnowledge
  } = useResultsData(answers);

  // Fallback if no result is provided
  const defaultResult: AssessmentResult = {
    urgency: "routine",
    category: "General Assessment",
    recommendation: "Please consult with a healthcare professional for proper evaluation.",
    analysis: "Assessment completed successfully.",
    nextSteps: ["Schedule an appointment with your GP"],
    professionalType: "General Practitioner",
    amIOKScore: 30,
    scientificReasoning: "Default assessment pending proper evaluation.",
    riskFactors: [],
    protectiveFactors: []
  };
  const finalResult = result || defaultResult;

  // Check if this is an emergency case with pre-calculated score
  const isEmergencyCase = finalResult.amIOKScore === 100;

  // For emergency cases, use the provided score immediately. For others, use AI-powered scoring
  const aiScore = isEmergencyCase ? 100 : enhancedData?.evidenceBasedScore?.score;
  const confidence = isEmergencyCase ? 100 : enhancedData?.evidenceBasedScore?.confidence ? Math.round(enhancedData.evidenceBasedScore.confidence * 100) : 85;

  // Show score immediately for emergency cases, wait for AI analysis for others
  const showScore = isEmergencyCase || !isLoadingKnowledge && aiScore !== undefined;
  const handleFindProvider = () => {
    // Direct to NHS website for finding GP/hospital
    window.open('https://www.nhs.uk/service-search/find-a-gp', '_blank');
  };
  const getAssessmentType = () => {
    if (answers.symptom_constellation?.category) {
      return `${answers.symptom_constellation.category} Assessment`;
    }
    return "Health Assessment";
  };
  const getLaymanDescription = (condition: string): string => {
    const descriptions: Record<string, string> = {
      "Gastroesophageal Reflux Disease (GERD)": "A condition where stomach acid frequently flows back into the esophagus, causing heartburn and acid indigestion. This happens when the muscle at the bottom of your esophagus doesn't close properly.",
      "Peptic Ulcer Disease": "Sores that develop on the inner lining of your stomach, upper small intestine, or esophagus, often causing stomach pain. Usually caused by bacteria (H. pylori) or certain medications.",
      "Functional Dyspepsia": "Recurring stomach pain or discomfort without an obvious cause, often related to how the stomach processes food. May feel like bloating, fullness, or burning sensation.",
      "Gastritis": "Inflammation of the stomach lining that can cause pain, nausea, and digestive discomfort. Often triggered by bacteria, medications, or excessive alcohol use.",
      "Irritable Bowel Syndrome": "A common disorder affecting the large intestine, causing cramping, abdominal pain, bloating, gas, and changes in bowel habits. It's a functional disorder with no structural damage.",
      "Migraine": "A type of headache that can cause severe throbbing pain, usually on one side of the head, often with nausea and sensitivity to light and sound. May last hours to days.",
      "Tension Headache": "A tension headache feels like a tight band or pressure around your head, often with mild to moderate pain in your forehead, temples, or back of the neck. It's commonly triggered by stress, poor posture, or lack of sleep and usually doesn't cause nausea or sensitivity to light like migraines do.",
      "Hypertension": "High blood pressure - when the force of blood against your artery walls is consistently too high. Often called the 'silent killer' because it usually has no symptoms.",
      "Anxiety Disorder": "A mental health condition characterized by excessive worry, fear, or nervousness that interferes with daily activities. Physical symptoms may include rapid heartbeat and sweating.",
      "Depression": "A mood disorder that causes persistent feelings of sadness, hopelessness, and loss of interest in activities you once enjoyed. Can affect sleep, appetite, and energy levels.",
      "Asthma": "A condition where airways narrow and swell, making breathing difficult and triggering coughing, wheezing, and shortness of breath. Often triggered by allergens or exercise.",
      "Bronchitis": "Inflammation of the airways leading to your lungs, causing persistent cough and difficulty breathing. Can be acute (short-term) or chronic (long-term).",
      "Upper Respiratory Infection": "A common cold or similar infection affecting the nose, throat, and upper airways. Usually viral and resolves on its own within 7-10 days.",
      "Pneumonia": "An infection that inflames air sacs in one or both lungs, which may fill with fluid causing cough, fever, and difficulty breathing. Can be bacterial, viral, or fungal.",
      "Urinary Tract Infection": "An infection in any part of your urinary system, commonly causing burning during urination, frequent urges to urinate, and cloudy or strong-smelling urine.",
      "Kidney Stones": "Hard deposits made of minerals and salts that form inside your kidneys, often causing severe pain in your back or side that may radiate to your groin.",
      "Arthritis": "Inflammation of one or more joints causing pain, stiffness, and reduced range of motion. Common types include osteoarthritis (wear and tear) and rheumatoid arthritis (autoimmune).",
      "Fibromyalgia": "A disorder characterized by widespread muscle pain, fatigue, and tender points throughout the body. Often accompanied by sleep problems and memory issues.",
      "Diabetes": "A group of diseases that result in too much sugar in the blood, affecting how your body processes blood glucose. Can lead to serious complications if not managed properly.",
      "Thyroid Disorder": "Conditions affecting the thyroid gland, which regulates metabolism, energy, and many body functions. Can cause symptoms like fatigue, weight changes, and mood issues.",
      "Tendonitis": "Inflammation or irritation of a tendon, the thick fibrous cord that attaches bone to muscle. It causes pain and tenderness just outside a joint and commonly affects shoulders, elbows, wrists, knees, and heels.",
      "Muscle Strain": "An injury to a muscle or tendon caused by overstretching or tearing of muscle fibers. Results in pain, swelling, and limited range of motion in the affected area.",
      "Sprain": "An injury to ligaments, the tough bands of fibrous tissue that connect bones to other bones. Occurs when joints are forced beyond their normal range of motion, causing stretching or tearing.",
      "Back Pain": "Discomfort or pain in the lower, middle, or upper back that can range from mild to severe. May be caused by muscle strain, herniated disc, arthritis, or poor posture.",
      "Joint Pain": "Discomfort, aches, and soreness in any of the body's joints, which can be caused by injury, arthritis, overuse, or underlying medical conditions affecting the joint structures."
    };

    // Try exact match first
    if (descriptions[condition]) {
      return descriptions[condition];
    }

    // Try partial matches for common conditions
    const lowerCondition = condition.toLowerCase();
    for (const [key, description] of Object.entries(descriptions)) {
      if (lowerCondition.includes(key.toLowerCase().split(' ')[0]) || key.toLowerCase().includes(lowerCondition.split(' ')[0])) {
        return description;
      }
    }

    // Create a basic medical definition for unknown conditions
    return `${condition} is a medical condition that involves specific symptoms and requires appropriate medical evaluation for proper diagnosis and treatment.`;
  };
  const getRiskMessage = () => {
    // Don't show any risk message until AI score is loaded
    if (!showScore) {
      return {
        icon: "⏳",
        title: "Analyzing...",
        message: "AI-powered analysis in progress",
        color: "gray"
      };
    }
    if (aiScore < 20) {
      return {
        icon: "✓",
        title: "Very Low Risk",
        message: "Continue monitoring your symptoms",
        color: "green"
      };
    } else if (aiScore < 40) {
      return {
        icon: "✓",
        title: "Minimal Concern",
        message: "Continue monitoring your symptoms",
        color: "green"
      };
    } else if (aiScore < 60) {
      return {
        icon: "⚠️",
        title: "Moderate Risk",
        message: "Schedule an appointment within a few days",
        color: "yellow"
      };
    } else if (aiScore < 80) {
      return {
        icon: "⚠️",
        title: "Elevated Risk",
        message: "Contact a healthcare provider soon",
        color: "orange"
      };
    } else {
      return {
        icon: "🏥",
        title: "High Priority",
        message: "Seek urgent medical attention",
        color: "red"
      };
    }
  };
  const riskInfo = getRiskMessage();
  return <div className="min-h-screen bg-gray-50">
      <ResultsHeader />

      <main className="container mx-auto px-3 py-3 max-w-md">
        <div className="space-y-4">
          {/* Brief Disclaimer */}
          <ClinicalDisclaimerPanel variant="brief" showEmergencyContacts={false} />

          {/* Main Assessment Results */}
          <div className="bg-white rounded-lg p-4 shadow-md border border-gray-100">
            <div className="text-center mb-4">
              <h1 className="text-xl font-bold text-gray-900 mb-1">Your Health Assessment</h1>
              <p className="text-xs text-gray-600 mb-3">AI-powered analysis using medical literature</p>
              {showScore && <div className="flex justify-center">
                  <AmIOKScoringExplanation aiScore={aiScore} confidence={confidence} reasoning={enhancedData?.evidenceBasedScore?.reasoning} />
                </div>}
            </div>


            {/* Mobile-first single column layout */}
            <div className="space-y-4">
              {/* Score Gauge */}
              <div className="flex justify-center">
                {showScore ? <RiskScoreGauge score={aiScore} confidence={confidence} size="medium" showDetails={true} className="shadow-sm w-full" /> : <div className="w-32 h-32 rounded-full border-4 border-gray-200 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-xs text-gray-600">Analyzing...</p>
                    </div>
                  </div>}
              </div>
              
              {/* Assessment Type */}
              <div className="text-center bg-blue-50 rounded-lg p-3">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Assessment Type</h3>
                <p className="text-blue-600 font-medium text-xs">{getAssessmentType()}</p>
              </div>

              {/* Risk Status Indicator */}
              <div className="text-center bg-gray-50 rounded-lg p-3">
                <div className="space-y-2">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${riskInfo.color === 'green' ? 'bg-green-100' : riskInfo.color === 'yellow' ? 'bg-yellow-100' : riskInfo.color === 'orange' ? 'bg-orange-100' : 'bg-red-100'}`}>
                    <span className="text-lg">{riskInfo.icon}</span>
                  </div>
                  <h2 className={`text-lg font-bold ${riskInfo.color === 'green' ? 'text-green-700' : riskInfo.color === 'yellow' ? 'text-yellow-700' : riskInfo.color === 'orange' ? 'text-orange-700' : 'text-red-700'}`}>{riskInfo.title}</h2>
                  <p className={`text-xs ${riskInfo.color === 'green' ? 'text-green-600' : riskInfo.color === 'yellow' ? 'text-yellow-600' : riskInfo.color === 'orange' ? 'text-orange-600' : 'text-red-600'}`}>{riskInfo.message}</p>
                </div>
              </div>

              {/* Find Provider Button */}
              {showScore && aiScore > 40 && <Button size="sm" className={`w-full py-3 text-sm font-semibold ${aiScore >= 80 ? 'bg-red-600 hover:bg-red-700' : aiScore >= 60 ? 'bg-orange-600 hover:bg-orange-700' : 'bg-yellow-600 hover:bg-yellow-700'}`} onClick={handleFindProvider}>
                  Find Healthcare Provider
                </Button>}
            </div>
          </div>

          {/* Symptoms Recap */}
          <SymptomsRecap answers={answers} />

          {/* Diagnostic Analysis Overview - Mobile Optimized */}
          {diagnosticAnalysis && <Card className="p-3 bg-white border shadow-sm">
              <h2 className="text-base font-bold text-gray-900 mb-3">We Think You Might Have...</h2>
              
              <div className="space-y-2">
                {diagnosticAnalysis.potentialDiagnoses.slice(0, 3).map((diagnosis, index) => <div key={index} className="p-2 border border-gray-200 rounded-md">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 text-xs leading-tight">{diagnosis.condition}</h4>
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${diagnosis.probability >= 60 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {diagnosis.probability}%
                        </span>
                      </div>
                      
                      
                      {/* Description Button */}
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="description" className="border-none">
                          <AccordionTrigger className="py-0.5 px-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:no-underline">
                            Description
                          </AccordionTrigger>
                          <AccordionContent className="pt-1 pb-0">
                            <div className="p-1.5 bg-blue-50 border border-blue-200 rounded-sm">
                              <p className="text-xs text-blue-800 leading-tight">
                                {diagnosis.laymanDescription || getLaymanDescription(diagnosis.condition)}
                              </p>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  </div>)}
              </div>
              
              <div className="mt-3 text-center">
                <DetailButton diagnosticAnalysis={diagnosticAnalysis} />
              </div>
            </Card>}

          {/* Next Steps Button */}
          <div className="text-center">
            <Button size="sm" onClick={() => navigate('/results/next-steps', {
            state: {
              answers,
              result: finalResult,
              enhancedData,
              aiScore,
              confidence
            }
          })} className="w-full py-3 text-sm font-semibold bg-blue-600 hover:bg-blue-700">
              What You Can Do Now
            </Button>
          </div>

          {/* Loading Indicator */}
          {isLoadingKnowledge && <Card className="p-3 bg-blue-50 border-blue-200">
              <div className="flex items-center space-x-2">
                <Database className="w-3 h-3 text-blue-600 animate-pulse flex-shrink-0" />
                <div>
                  <p className="text-blue-800 font-medium text-xs">Analyzing Medical Knowledge</p>
                  <p className="text-blue-600 text-xs">Cross-referencing research...</p>
                </div>
              </div>
            </Card>}

          {/* Full Disclaimer */}
          <ClinicalDisclaimerPanel variant="full" showEmergencyContacts={true} />
        </div>
      </main>
    </div>;
};
export default ResultsPage1;